import React from 'react'
import { useSearchParams } from 'react-router-dom'
import { Bar, BarChart, CartesianGrid, Cell, Pie, PieChart, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts'
import {
  BarChart3,
  Building2,
  Car,
  ClipboardList,
  ExternalLink,
  HeartPulse,
  Landmark,
  Lightbulb,
  MessageSquareText,
  Newspaper,
  PiggyBank,
  RefreshCw,
  ShieldCheck,
  TrendingUp,
  UsersRound,
} from 'lucide-react'
import { api, getApiErrorMessage } from '../lib/api'
import DiseaseMedicalFeeStatsPage from './DiseaseMedicalFeeStats'
import InsuranceNewsPage from './InsuranceNews'

const INLINE_ERROR_REQUEST_CONFIG = { __silent: true, __suppressErrorPageRedirect: true }
const CHART_COLORS = ['#0f766e', '#2563eb', '#d97706', '#7c3aed', '#be123c', '#0891b2']
const EMPTY_LABEL = '미분류'
const RAW_FIELD_LABELS = {
  joincnt: '가입건수',
  joincount: '가입건수',
  subscnt: '가입건수',
  subscntcnt: '가입건수',
  cntrcnt: '계약건수',
  ctrcnt: '계약건수',
  cntrctcnt: '계약건수',
  contractcount: '계약건수',
  contractcnt: '계약건수',
  cnt: '건수',
  vhclcnt: '가입대수',
  carcnt: '가입대수',
  accdcnt: '사고건수',
  losscnt: '손해건수',
  lossamt: '손해액',
  injrycnt: '부상건수',
  deathcnt: '사망건수',
  pprcnt: '인원수',
  inpm: '보험료',
  premamt: '보험료',
  premium: '보험료',
  premiumamount: '보험료',
  sumprem: '보험료',
  earnedprem: '경과보험료',
  elprem: '경과보험료',
  elpsinpm: '경과보험료',
  amt: '금액',
  payamt: '지급금액',
  insamt: '보험가입금액',
  losamt: '손해액',
  injptlcnt: '부상건수',
  dthtotlcnt: '사망건수',
  nope: '인원수',
  stragcscr: '점포/대리점 수',
  tastamt: '총자산',
  oselfcpsaamt: '자기자본',
  pymtcpsaamt: '납입자본금',
  shrownrto: '지분율',
  joinrate: '가입률',
  joinrto: '가입률',
  joinrt: '가입률',
  subsrto: '가입률',
  cntrrto: '계약률',
  contractrto: '계약률',
  rate: '비율',
  ratio: '비율',
  rto: '비율',
  baseym: '기준년월',
  basym: '기준년월',
  stndym: '기준년월',
  crtrym: '기준년월',
  basyr: '기준년도',
  stndyr: '기준년도',
  xcsmplnpncnt: '임직원/설계사 수',
}

const RAW_ALIAS_GROUPS = {
  basePeriod: ['basYm', 'baseYm', 'stndYm', 'crtrYm', 'basYmd', 'basDt', 'isuCmpyOfrYm', 'atmbAcdnCnlsYm', 'sttsAccmlTrgtYr', 'basYr', 'baseYear', 'crtrYr', 'year', 'stndYr'],
  region: ['ctpvNm', 'ctpv', 'sidoNm', 'siDoNm', 'regionName', 'region', 'areaNm', 'rgnNm', 'lctnNm', '시도', '지역'],
  gender: ['gndrNm', 'sexNm', 'genderName', 'gndrDcdNm', 'sexDcdNm', 'gender', 'sex', 'gndrCd', 'sexCd', '성별'],
  age: ['arrvAge', 'dodAge', 'rchnAggr', 'aggr', 'ageGroup', 'ageGrp', 'age', 'agrde', 'agrdeNm', 'reachedAge', 'ageDcd', 'ageDcdNm', '도달연령', '연령', '연령대'],
  insuranceType: ['isuKindNm', 'isuItmsNm', 'insrncKndNm', 'insKndNm', 'insuKndNm', 'insrncPrdctNm', 'insuranceType', 'insuranceKind', 'gdNm', 'productName', 'insGdNm', '상품명', '보험종류', '보험종목'],
  coverageType: ['grnteeTyNm', 'mogClsfNm', 'coverageType', 'coverageTypeName', 'damboNm', 'guaranteeName', 'bojangGbnNm', '담보구분', '담보명'],
  objectType: ['vhclTyNm', 'carKndNm', 'carType', 'vhclKndNm', 'objcClsfNm', 'objTyNm', 'atmbPlorNm', 'kncrNm', 'deadInjryClsfNm', 'dthInjClsfNm', 'objectType', 'objectTypeName', 'mulgeonGbnNm', 'itemName', 'objectName', 'korFrgnCarNm', '차종', '물건구분'],
  category: ['taxPrqlYn', 'taxQualfYn', 'taxQualifiedYn', 'payMethodNm', 'payMethod', 'pymtMthNm', 'pymMthdNm', 'pymnMthdNm', 'offrTyNm', 'recruitTypeNm', 'recruitType', 'rcrtMthdNm', 'yerUnitPymtTerm', 'payPeriodNm', 'pymPrdNm', 'clNm', 'itemNm', 'categoryName', '연금구분', '납입방법', '모집형태'],
  companyName: ['fncoNm', 'cmpyNm', 'insCmpyNm', 'companyName', 'corpNm', 'fncoKorNm', '보험사명', '회사명'],
  metricValue: ['joinCnt', 'subsCnt', 'cntrCnt', 'ctrCnt', 'cntrctCnt', 'contractCount', 'contractCnt', 'vhclCnt', 'carCnt', 'cnt', 'joinRto', 'joinRate', 'rate', 'rto', 'ratio', 'premAmt', 'premiumAmount', 'inpm', 'earnedPrem', 'elPrem', 'elpsInpm', 'losAmt', 'injPtlCnt', 'dthTotlCnt', 'nope', 'amt'],
}

const MARKET_PRIMARY_METRICS = {
  lifeJoinStatus: ['가입건수', '계약건수', '가입률', '건수'],
  individualAnnuity: ['가입건수', '계약건수', '가입률', '건수'],
  autoContract: ['가입대수', '계약건수', '가입건수', '건수'],
}

const INSIGHT_TABS = [
  {
    id: 'disease-medical-fee',
    label: '질병 진료비',
    icon: HeartPulse,
    source: '심평원 질병 진료비 통계',
    cadence: '활성화된 수집 데이터 기준',
    collectionMode: '기존 질병 진료비 통계 수집 데이터 사용',
    dataMode: 'diseaseMedicalFeeStats',
    metrics: ['KCD별 총진료비', '환자수/진료건수', '관련 보장 항목'],
    placements: ['질병별 상담 근거', '보장 검토 메모', '고객 제시용 진료비 인사이트'],
  },
  {
    id: 'life',
    label: '생명보험',
    icon: ShieldCheck,
    source: '금융위원회 생명보험가입정보',
    sourceUrl: 'https://www.data.go.kr/data/15124892/openapi.do',
    cadence: '월 1회 수집 권장',
    collectionMode: '공공데이터포털 API 자동 수집',
    dataMode: 'market',
    datasetType: 'lifeJoinStatus',
    metrics: ['보험종류별 가입건수', '가입률', '연령/성별/지역별 가입 현황'],
    placements: ['보장성보험 준비가 약한 고객군을 찾아 상담 우선순위 잡기', '연령대/성별 가입 현황을 현재 보장 수준 질문으로 연결', '가입률이 낮은 구간은 보장 공백 점검 니즈로 전환'],
  },
  {
    id: 'annuity',
    label: '개인연금',
    icon: PiggyBank,
    source: '금융위원회 생명보험가입정보',
    sourceUrl: 'https://www.data.go.kr/data/15124892/openapi.do',
    cadence: '월 1회 수집 권장',
    collectionMode: '생명보험 가입현황 API에서 함께 자동 수집',
    dataMode: 'market',
    datasetType: 'individualAnnuity',
    metrics: ['연령별 개인연금 가입건수', '세제적격 구분', '납입방법/모집형태별 가입건수'],
    placements: ['연령대별 연금 준비 현황으로 은퇴 준비 필요성 환기', '세제적격/납입방법 차이를 절세·장기납입 니즈 질문으로 연결', '가입이 늦어지는 고객군은 납입 가능 기간과 목표 연금액 점검으로 전환'],
  },
  {
    id: 'nonlife',
    label: '손해보험',
    icon: Landmark,
    source: 'KOSIS 손해보험사별 계약 및 손해상황',
    sourceUrl: 'https://kosis.kr/statHtml/statHtml.do?orgId=382&tblId=TX_38202_A159',
    cadence: '월 1회 또는 분기 1회 수집 권장',
    collectionMode: 'KOSIS Open API 자동 수집',
    dataMode: 'kosisNonlife',
    metrics: ['보험사별 계약건수', '보험종목별 수입보험료', '보험가입금액/손해상황'],
    placements: ['손보 시장 규모 비교', '보험종목별 성장 추세', '보험사 비교 리포트'],
  },
  {
    id: 'auto',
    label: '자동차보험',
    icon: Car,
    source: '금융위원회 자동차보험가입정보',
    sourceUrl: 'https://www.data.go.kr/data/15124891/openapi.do',
    cadence: '월 1회 수집 권장',
    collectionMode: '공공데이터포털 API 자동 수집',
    dataMode: 'market',
    datasetType: 'autoContract',
    metrics: ['차종별 가입대수', '담보구분별 가입대수', '연령/성별 계약 현황'],
    placements: ['자동차보험 상담 보조', '차종/연령별 시장 현황', '담보별 가입 경향 참고'],
  },
  {
    id: 'general',
    label: '일반손해보험',
    icon: Building2,
    source: '금융위원회 일반손해보험가입정보',
    sourceUrl: 'https://www.data.go.kr/data/15124894/openapi.do',
    cadence: '월 1회 수집 권장',
    collectionMode: '기존 공공데이터 수집 구조와 연결',
    dataMode: 'general',
    metrics: ['담보구분별 계약건수', '물건구분별 보험료 총액', '기준년도별 일반보험 규모'],
    placements: ['기업/재물보험 상담 근거', '공장/창고 등 물건 유형별 시장 규모', '일반보험 화면 BE 연동 후보'],
  },
  {
    id: 'channel',
    label: '판매채널',
    icon: UsersRound,
    source: '생명보험협회 판매통계',
    sourceUrl: 'https://www.klia.or.kr/consumer/stats/statHomSta/monthStats.do?viewGb=sale',
    cadence: '월 1회 자동 수집 또는 운영자 업로드',
    collectionMode: '협회 HTML XLS 업로드 + 자동 수집',
    dataMode: 'lifeSalesChannel',
    metrics: ['설계사 채널 판매 규모', '대리점/방카/CM/TM 판매 규모', '채널별 전월/전년 대비'],
    placements: [
      '고객 유입 채널별 시장 흐름을 리더/운영자 리포트에 반영',
      '설계사·대리점·방카·CM 비중을 비교해 조직별 영업 전략 점검',
      '보장성/저축성 상품의 채널 차이를 교육·캠페인 주제로 연결',
    ],
  },
  {
    id: 'insurer',
    label: '보험사 비교',
    icon: BarChart3,
    source: '금융위 보험사 일반현황 + KOSIS',
    sourceUrl: 'https://www.data.go.kr/data/15061307/openapi.do',
    cadence: '월 1회 또는 분기 1회 수집 권장',
    collectionMode: '금융위 API 자동 수집 + KOSIS 보강',
    dataMode: 'insurer',
    metrics: ['보험사별 계약건수', '수입보험료', '주요 영업/재무 지표'],
    placements: ['보험사 비교 리포트', '시장 점유 흐름', '운영자 인사이트'],
  },
  {
    id: 'news',
    label: '보험뉴스',
    icon: Newspaper,
    source: '보험뉴스/RSS 큐레이션',
    cadence: '상시',
    collectionMode: '기존 보험뉴스/RSS 큐레이션 사용',
    dataMode: 'news',
    metrics: ['게시 뉴스', '승인 대기 뉴스', '일간 다이제스트'],
    placements: ['시장 이슈 확인', '설계사 공유 콘텐츠', '영업 대화 소재'],
  },
]

const WORKBENCH_TAB = {
  id: 'counseling',
  label: '상담 인사이트',
  icon: MessageSquareText,
  source: '보험시장 인사이트 통합',
  cadence: '수집 데이터 기준',
  collectionMode: '수집 데이터 통합 조회',
  dataMode: 'workbench',
}

const NAV_TABS = [WORKBENCH_TAB, ...INSIGHT_TABS]

const WORKBENCH_TOPIC_OPTIONS = [
  { id: 'all', label: '전체' },
  { id: 'health', label: '건강/질병' },
  { id: 'pension', label: '연금' },
  { id: 'property', label: '화재/일반보험' },
  { id: 'market', label: '시장/채널' },
]

const WORKBENCH_CUSTOMER_OPTIONS = [
  { id: 'all', label: '전체 고객' },
  { id: 'young-family', label: '가족 보장' },
  { id: 'pre-retiree', label: '은퇴 준비' },
  { id: 'business', label: '사업자' },
]

const WORKBENCH_CONTEXT_OPTIONS = [
  { id: 'consulting', label: '상담' },
  { id: 'report', label: '리포트' },
  { id: 'briefing', label: '브리핑' },
]

function unwrapApiData(res) {
  const body = res?.data
  if (body?.success === false) throw new Error(body?.message || '요청 처리에 실패했습니다.')
  return body?.data !== undefined ? body.data : body
}

function formatNumber(value) {
  if (value === null || value === undefined || value === '') return '-'
  const n = Number(value)
  if (!Number.isFinite(n)) return String(value)
  return n.toLocaleString('ko-KR')
}

function formatPeriod(value) {
  const s = String(value || '').trim()
  if (!s) return '-'
  if (/^\d{6}$/.test(s)) return `${s.slice(0, 4)}.${s.slice(4)}`
  return s
}

function toNumber(value) {
  if (value === null || value === undefined || value === '') return 0
  const n = Number(String(value).replace(/,/g, '').replace(/%/g, '').trim())
  return Number.isFinite(n) ? n : 0
}

function normalizeLabelKey(value) {
  return String(value || '').replace(/[^A-Za-z0-9가-힣]/g, '').toLowerCase()
}

function isBlankGroupLabel(value) {
  const s = String(value ?? '').trim()
  if (!s) return true
  const normalized = s.toLowerCase()
  return normalized === '-' || normalized === 'null' || normalized === 'undefined' || normalized === EMPTY_LABEL
}

function fallbackMetricLabel(value) {
  const raw = String(value || '').trim()
  if (!raw) return ''
  const key = normalizeLabelKey(raw)
  if (RAW_FIELD_LABELS[key]) return RAW_FIELD_LABELS[key]
  if (/[가-힣]/.test(raw)) return raw
  if (key.endsWith('rate') || key.endsWith('rt') || key.endsWith('ratio')) return '비율'
  if (key.endsWith('yn')) return '여부'
  return raw
}

function displayLabel(value, kind = 'dimension') {
  const raw = String(value ?? '').trim()
  if (isBlankGroupLabel(raw)) return ''
  const key = normalizeLabelKey(raw)
  if (kind === 'metric') return fallbackMetricLabel(raw)
  if (RAW_FIELD_LABELS[key]) return RAW_FIELD_LABELS[key]
  if (kind === 'gender' && (key === 'm' || key === 'male' || raw === '1')) return '남성'
  if (kind === 'gender' && (key === 'f' || key === 'female' || raw === '2')) return '여성'
  return raw
}

function parseRawJson(value) {
  if (!value) return null
  if (typeof value === 'object') return value
  try {
    return JSON.parse(String(value))
  } catch {
    return null
  }
}

function rawValue(raw, aliases) {
  if (!raw || !aliases?.length) return ''
  const entries = Object.entries(raw)
  const normalizedAliases = aliases.map(normalizeLabelKey)
  for (const alias of normalizedAliases) {
    const hit = entries.find(([key, value]) => normalizeLabelKey(key) === alias && value !== null && value !== undefined && String(value).trim() !== '')
    if (hit) return hit[1]
  }
  return ''
}

function rowValue(row, rowKey, raw, aliasKey) {
  const direct = row?.[rowKey]
  if (!isBlankGroupLabel(direct)) return direct
  return rawValue(raw, RAW_ALIAS_GROUPS[aliasKey] || [])
}

function metricLabel(row) {
  return displayLabel(row?.displayMetricName || row?.metricName || row?.metricCode, 'metric')
}

function companyLabel(row) {
  return displayLabel(row?.displayCompanyName || row?.companyName)
}

function metricValue(row) {
  if (row?.metricValue !== null && row?.metricValue !== undefined && row?.metricValue !== '') {
    return toNumber(row.metricValue)
  }
  if (row?.metricValueText !== null && row?.metricValueText !== undefined && row?.metricValueText !== '') {
    return toNumber(row.metricValueText)
  }
  const raw = row?.__raw || parseRawJson(row?.rawJson)
  return toNumber(rawValue(raw, RAW_ALIAS_GROUPS.metricValue))
}

function groupByRows(rows, getLabel, getValue = metricValue) {
  const totals = new Map()
  ;(Array.isArray(rows) ? rows : []).forEach((row) => {
    const label = displayLabel(getLabel(row))
    const value = toNumber(getValue(row))
    if (!label || value <= 0) return
    totals.set(label, (totals.get(label) || 0) + value)
  })
  return Array.from(totals.entries())
    .map(([label, metricTotal]) => ({ label, metricTotal }))
    .sort((a, b) => b.metricTotal - a.metricTotal)
}

function groupMetricRows(rows) {
  const totals = new Map()
  ;(Array.isArray(rows) ? rows : []).forEach((row) => {
    const label = metricLabel(row)
    const value = metricValue(row)
    if (!label || value <= 0) return
    totals.set(label, (totals.get(label) || 0) + value)
  })
  return Array.from(totals.entries())
    .map(([label, metricTotal]) => ({ label, metricTotal }))
    .sort((a, b) => b.metricTotal - a.metricTotal)
}

function sumMetricRows(rows) {
  return (Array.isArray(rows) ? rows : []).reduce((sum, row) => sum + metricValue(row), 0)
}

function serverGroupRows(rows, labelKind) {
  return (Array.isArray(rows) ? rows : [])
    .map((row) => ({
      label: displayLabel(row?.label, labelKind),
      metricTotal: toNumber(row?.metricTotal ?? row?.contractCount ?? row?.premiumAmount ?? row?.value),
      rowCount: toNumber(row?.rowCount),
    }))
    .filter((row) => row.label && row.metricTotal > 0)
    .sort((a, b) => b.metricTotal - a.metricTotal)
}

function preferServerGroup(serverRows, fallbackRows, labelKind) {
  const normalized = serverGroupRows(serverRows, labelKind)
  return normalized.length ? normalized : fallbackRows
}

function normalizeMarketItem(row) {
  const raw = parseRawJson(row?.rawJson)
  return {
    ...row,
    __raw: raw,
    displayBasePeriod: rowValue(row, 'basePeriod', raw, 'basePeriod'),
    displayRegionName: displayLabel(rowValue(row, 'regionName', raw, 'region')),
    displayGenderName: displayLabel(rowValue(row, 'genderName', raw, 'gender'), 'gender'),
    displayAgeGroup: displayLabel(rowValue(row, 'ageGroup', raw, 'age')),
    displayInsuranceType: displayLabel(rowValue(row, 'insuranceType', raw, 'insuranceType')),
    displayCoverageType: displayLabel(rowValue(row, 'coverageType', raw, 'coverageType')),
    displayObjectType: displayLabel(rowValue(row, 'objectType', raw, 'objectType')),
    displayCategoryName: displayLabel(rowValue(row, 'categoryName', raw, 'category')),
    displayMetricName: displayLabel(row?.metricName || row?.metricCode, 'metric'),
  }
}

function primaryMarketRows(rows, datasetType) {
  const preferred = MARKET_PRIMARY_METRICS[datasetType] || ['가입건수', '계약건수', '차량대수', '건수']
  for (const label of preferred) {
    const matches = rows.filter((row) => metricLabel(row) === label)
    if (matches.length) return matches
  }
  return rows.filter((row) => metricValue(row) > 0)
}

function normalizeMarketData(tab, data) {
  const items = (Array.isArray(data?.items) ? data.items : []).map(normalizeMarketItem)
  const primaryRows = primaryMarketRows(items, tab.datasetType)
  const serverPrimaryMetricLabel = displayLabel(data?.primaryMetricName, 'metric')
  const primaryMetricLabel = serverPrimaryMetricLabel || metricLabel(primaryRows[0]) || metricLabel(items[0]) || '대표 지표'
  const serverPrimaryTotal = toNumber(data?.primaryMetricTotal ?? data?.primaryOverview?.metricTotal)
  return {
    ...data,
    items,
    primaryRows,
    primaryMetricLabel,
    primaryMetricTotal: serverPrimaryTotal || sumMetricRows(primaryRows),
    byMetric: preferServerGroup(data?.byMetric, groupMetricRows(items), 'metric'),
    byRegion: preferServerGroup(data?.byRegion, groupByRows(primaryRows, (row) => row.displayRegionName)),
    byGender: preferServerGroup(data?.byGender, groupByRows(primaryRows, (row) => row.displayGenderName), 'gender'),
    byAgeGroup: preferServerGroup(data?.byAgeGroup, groupByRows(primaryRows, (row) => row.displayAgeGroup)),
    byInsuranceType: preferServerGroup(data?.byInsuranceType, groupByRows(primaryRows, (row) => row.displayInsuranceType)),
    byCoverageType: preferServerGroup(data?.byCoverageType, groupByRows(primaryRows, (row) => row.displayCoverageType)),
    byObjectType: preferServerGroup(data?.byObjectType, groupByRows(primaryRows, (row) => row.displayObjectType)),
    byCategory: preferServerGroup(data?.byCategory, groupByRows(primaryRows, (row) => row.displayCategoryName)),
  }
}

function normalizeGeneralItem(row) {
  const raw = parseRawJson(row?.rawJson)
  return {
    ...row,
    __raw: raw,
    displayCoverageType: displayLabel(rowValue(row, 'coverageType', raw, 'coverageType')),
    displayObjectType: displayLabel(rowValue(row, 'objectType', raw, 'objectType')),
  }
}

function groupGeneralRows(rows, labelKey, valueKey) {
  return groupByRows(rows, (row) => row[labelKey], (row) => row[valueKey])
    .map((row) => ({ ...row, [valueKey]: row.metricTotal }))
}

function normalizeGeneralData(data) {
  const items = (Array.isArray(data?.items) ? data.items : []).map(normalizeGeneralItem)
  return {
    ...data,
    items,
    byCoverageType: groupGeneralRows(items, 'displayCoverageType', 'contractCount'),
    byObjectType: groupGeneralRows(items, 'displayObjectType', 'premiumAmount'),
  }
}

function normalizeKosisNonlifeItem(row) {
  const raw = parseRawJson(row?.rawJson)
  return {
    ...row,
    __raw: raw,
    displayBasePeriod: rowValue(row, 'basePeriod', raw, 'basePeriod'),
    displayCompanyName: displayLabel(row?.companyName || raw?.C1_NM),
    displayInsuranceTypeName: displayLabel(row?.insuranceTypeName || raw?.C2_NM),
    displayStatusName: displayLabel(row?.statusName || raw?.C3_NM),
    displayMetricName: displayLabel(row?.metricName || raw?.ITM_NM || row?.metricCode, 'metric'),
  }
}

function normalizeKosisNonlifeData(data) {
  const items = (Array.isArray(data?.items) ? data.items : []).map(normalizeKosisNonlifeItem)
  return {
    ...data,
    items,
    byCompany: Array.isArray(data?.byCompany) ? data.byCompany : [],
    byInsuranceType: Array.isArray(data?.byInsuranceType) ? data.byInsuranceType : [],
    byMetric: Array.isArray(data?.byMetric) ? data.byMetric : [],
  }
}

function normalizeLifeSalesChannelItem(row) {
  const raw = parseRawJson(row?.rawJson)
  return {
    ...row,
    __raw: raw,
    displayBaseYearMonth: rowValue(row, 'baseYearMonth', raw, 'basePeriod'),
    displayMetricName: displayLabel(rowValue(row, 'metricName', raw, 'metric'), 'metric'),
    displayProductCategory: displayLabel(rowValue(row, 'productCategory', raw, 'category')),
    displayProductDetail: displayLabel(row?.productDetail || raw?.productDetail),
    displayChannelName: displayLabel(row?.channelName || raw?.channelName),
  }
}

function normalizeLifeSalesChannelData(data) {
  const items = (Array.isArray(data?.items) ? data.items : []).map(normalizeLifeSalesChannelItem)
  return {
    ...data,
    items,
    byChannel: Array.isArray(data?.byChannel) ? data.byChannel : [],
    byProduct: Array.isArray(data?.byProduct) ? data.byProduct : [],
  }
}

function normalizeInsurerItem(row) {
  const raw = parseRawJson(row?.rawJson)
  return {
    ...row,
    __raw: raw,
    displayCompanyName: displayLabel(rowValue(row, 'companyName', raw, 'companyName')),
    displayMetricName: displayLabel(row?.metricName || row?.metricCode, 'metric'),
  }
}

function normalizeInsurerData(data) {
  return {
    life: {
      ...(data?.life || {}),
      items: (Array.isArray(data?.life?.items) ? data.life.items : []).map(normalizeInsurerItem),
    },
    nonlife: {
      ...(data?.nonlife || {}),
      items: (Array.isArray(data?.nonlife?.items) ? data.nonlife.items : []).map(normalizeInsurerItem),
    },
  }
}

function compactNumber(value) {
  const n = toNumber(value)
  if (!n) return '0'
  if (Math.abs(n) >= 100000000) return `${(n / 100000000).toLocaleString('ko-KR', { maximumFractionDigits: 1 })}억`
  if (Math.abs(n) >= 10000) return `${(n / 10000).toLocaleString('ko-KR', { maximumFractionDigits: 1 })}만`
  return n.toLocaleString('ko-KR')
}

function valueOf(item, key = 'metricTotal') {
  return toNumber(item?.[key])
}

function labelOf(item, kind = 'dimension') {
  const raw = typeof item === 'object' ? item?.label : item
  return displayLabel(raw, kind) || EMPTY_LABEL
}

function topRows(items, valueKey = 'metricTotal', count = 6, labelKind = 'dimension') {
  const totals = new Map()
  ;(Array.isArray(items) ? items : []).forEach((item) => {
    const label = labelOf(item, labelKind)
    const value = valueOf(item, valueKey)
    if (!label || label === EMPTY_LABEL || value <= 0) return
    totals.set(label, (totals.get(label) || 0) + value)
  })
  return Array.from(totals.entries())
    .map(([label, value]) => ({ label, [valueKey]: value }))
    .sort((a, b) => valueOf(b, valueKey) - valueOf(a, valueKey))
    .slice(0, count)
}

function topRow(items, valueKey = 'metricTotal', labelKind = 'dimension') {
  return topRows(items, valueKey, 1, labelKind)[0] || null
}

function isNeedsFocusedMarketTab(tab) {
  return tab?.id === 'life' || tab?.id === 'annuity'
}

function periodRange(overview, fromKey, toKey) {
  const from = formatPeriod(overview?.[fromKey])
  const to = formatPeriod(overview?.[toKey])
  if (from === '-' && to === '-') return '-'
  if (from === to) return from
  return `${from} ~ ${to}`
}

function versionLabel(version) {
  if (!version) return '수집 데이터 없음'
  return version.sourceVersion || `#${version.id}`
}

function versionDate(version) {
  if (!version?.importedAt) return '-'
  return String(version.importedAt).replace('T', ' ').slice(0, 16)
}

function StatBox({ label, value, helper, tone = 'navy' }) {
  const toneClass = {
    navy: 'border-brand-navy/20 bg-sky-50/70 text-brand-navy',
    teal: 'border-teal-200 bg-teal-50 text-teal-800',
    amber: 'border-amber-200 bg-amber-50 text-amber-800',
    slate: 'border-slate-200 bg-slate-50 text-slate-800',
  }[tone] || 'border-slate-200 bg-slate-50 text-slate-800'
  return (
    <div className={`min-h-[5.5rem] rounded border px-3 py-3 ${toneClass}`}>
      <div className="text-xs font-semibold opacity-70">{label}</div>
      <div className="mt-1 break-words text-lg font-bold leading-tight">{value}</div>
      {helper ? <div className="mt-1 text-xs opacity-75">{helper}</div> : null}
    </div>
  )
}

function ChartTooltip({ active, payload, label }) {
  if (!active || !payload?.length) return null
  const value = payload[0]?.value
  return (
    <div className="rounded border border-slate-200 bg-white px-3 py-2 text-xs shadow-lg">
      <div className="font-semibold text-slate-900">{label || payload[0]?.name}</div>
      <div className="mt-1 text-slate-600">{formatNumber(value)}</div>
    </div>
  )
}

function SectionHeader({ icon: Icon, title, right }) {
  return (
    <div className="mb-3 flex min-w-0 items-center justify-between gap-3">
      <div className="flex min-w-0 items-center gap-2">
        {Icon ? <Icon className="h-4 w-4 shrink-0 text-brand-navy" aria-hidden="true" /> : null}
        <h3 className="min-w-0 truncate text-sm font-bold text-slate-900">{title}</h3>
      </div>
      {right ? <div className="shrink-0">{right}</div> : null}
    </div>
  )
}

function BarDistribution({ title, items, valueKey = 'metricTotal', labelKind = 'dimension', emptyText = '표시할 구분값이 없습니다.' }) {
  const rows = topRows(items, valueKey, 7, labelKind).map((item) => ({
    name: labelOf(item, labelKind),
    value: valueOf(item, valueKey),
  }))
  return (
    <section className="rounded-lg border border-slate-200 bg-white p-4 shadow-sm">
      <SectionHeader icon={BarChart3} title={title} />
      {rows.length === 0 ? (
        <div className="flex h-64 items-center justify-center rounded border border-dashed border-slate-200 px-4 text-center text-sm text-slate-500">{emptyText}</div>
      ) : (
        <div className="h-64">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={rows} layout="vertical" margin={{ top: 8, right: 16, bottom: 8, left: 88 }}>
              <CartesianGrid strokeDasharray="3 3" horizontal={false} stroke="#e2e8f0" />
              <XAxis type="number" tickFormatter={compactNumber} tick={{ fontSize: 11 }} stroke="#94a3b8" />
              <YAxis type="category" dataKey="name" width={86} tick={{ fontSize: 11 }} stroke="#64748b" />
              <Tooltip content={<ChartTooltip />} />
              <Bar dataKey="value" radius={[0, 6, 6, 0]}>
                {rows.map((entry, index) => (
                  <Cell key={entry.name} fill={CHART_COLORS[index % CHART_COLORS.length]} />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>
      )}
    </section>
  )
}

function DonutDistribution({ title, items, valueKey = 'metricTotal', labelKind = 'dimension', emptyText = '표시할 구분값이 없습니다.' }) {
  const rows = topRows(items, valueKey, 5, labelKind).map((item) => ({
    name: labelOf(item, labelKind),
    value: valueOf(item, valueKey),
  }))
  return (
    <section className="rounded-lg border border-slate-200 bg-white p-4 shadow-sm">
      <SectionHeader icon={TrendingUp} title={title} />
      {rows.length === 0 ? (
        <div className="flex h-64 items-center justify-center rounded border border-dashed border-slate-200 px-4 text-center text-sm text-slate-500">{emptyText}</div>
      ) : (
        <div className="grid gap-3 md:grid-cols-[minmax(0,1fr)_12rem]">
          <div className="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Tooltip content={<ChartTooltip />} />
                <Pie data={rows} dataKey="value" nameKey="name" innerRadius={54} outerRadius={86} paddingAngle={2}>
                  {rows.map((entry, index) => (
                    <Cell key={entry.name} fill={CHART_COLORS[index % CHART_COLORS.length]} />
                  ))}
                </Pie>
              </PieChart>
            </ResponsiveContainer>
          </div>
          <div className="flex flex-col justify-center gap-2">
            {rows.map((item, index) => (
              <div key={item.name} className="flex min-w-0 items-center gap-2 text-sm">
                <span className="h-2.5 w-2.5 shrink-0 rounded-sm" style={{ backgroundColor: CHART_COLORS[index % CHART_COLORS.length] }} />
                <span className="min-w-0 flex-1 truncate text-slate-700">{item.name}</span>
                <span className="shrink-0 font-semibold text-slate-900">{compactNumber(item.value)}</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </section>
  )
}

function InsightCardGrid({ insights }) {
  const items = Array.isArray(insights) ? insights.filter(Boolean) : []
  if (!items.length) return null
  return (
    <section className="grid gap-3 lg:grid-cols-3">
      {items.map((item) => {
        const Icon = item.icon || Lightbulb
        return (
          <div key={item.title} className="rounded-lg border border-slate-200 bg-white p-4 shadow-sm">
            <div className="flex items-start gap-3">
              <div className={`flex h-9 w-9 shrink-0 items-center justify-center rounded ${item.iconTone || 'bg-amber-50 text-amber-700'}`}>
                <Icon className="h-4 w-4" aria-hidden="true" />
              </div>
              <div className="min-w-0">
                <div className="text-sm font-bold text-slate-950">{item.title}</div>
                <div className="mt-1 text-sm leading-6 text-slate-700">{item.body}</div>
              </div>
            </div>
          </div>
        )
      })}
    </section>
  )
}

function UsagePlaybook({ tab, focusLabel, points }) {
  const playbookPoints = (Array.isArray(points) ? points : (tab.placements || [])).slice(0, 3)
  const normalizedPoints = playbookPoints.map((point) => (
    typeof point === 'string' ? { title: point } : point
  )).filter((point) => point?.title)
  const pointFocusLabel = focusLabel || ''
  if (!normalizedPoints.length) return null
  return (
    <section className="rounded-lg border border-slate-200 bg-white p-4 shadow-sm">
      <SectionHeader icon={MessageSquareText} title="상담 활용 포인트" />
      <div className="grid gap-3 lg:grid-cols-3">
        {normalizedPoints.map((point, index) => (
          <div key={`${point.title}-${index}`} className="rounded border border-slate-100 bg-slate-50 px-3 py-3">
            <div className="text-xs font-semibold text-slate-500">활용 {index + 1}</div>
            <div className="mt-1 text-sm font-semibold text-slate-900">{point.title}</div>
            {point.body ? <div className="mt-2 text-xs leading-5 text-slate-600">{point.body}</div> : null}
            {pointFocusLabel && !point.body ? <div className="mt-2 text-xs text-slate-600">현재 데이터 포인트: {pointFocusLabel}</div> : null}
          </div>
        ))}
      </div>
    </section>
  )
}

function MarketDistributionGrid({ tab, data }) {
  const fallbackMetricName = isNeedsFocusedMarketTab(tab) ? '가입 현황' : '대표 지표'
  const metricName = data?.primaryMetricLabel && data.primaryMetricLabel !== '대표 지표'
    ? data.primaryMetricLabel
    : fallbackMetricName
  if (tab.id === 'annuity') {
    return (
      <div className="grid gap-4 xl:grid-cols-2">
        <BarDistribution title={`연령대별 ${metricName}`} items={data.byAgeGroup} emptyText="원천 데이터에 연령 구분값이 없습니다." />
        <DonutDistribution title="세제/납입/모집 구분" items={data.byCategory} emptyText="원천 데이터에 세제·납입·모집 구분값이 없습니다." />
      </div>
    )
  }
  if (tab.id === 'auto') {
    return (
      <div className="grid gap-4 xl:grid-cols-2">
        <BarDistribution title={`차종별 ${metricName}`} items={data.byObjectType} emptyText="원천 데이터에 차종 구분값이 없습니다." />
        <BarDistribution title={`담보구분별 ${metricName}`} items={data.byCoverageType} emptyText="원천 데이터에 담보 구분값이 없습니다." />
        <BarDistribution title={`연령대별 ${metricName}`} items={data.byAgeGroup} emptyText="원천 데이터에 연령 구분값이 없습니다." />
        <DonutDistribution title={`성별 ${metricName}`} items={data.byGender} emptyText="원천 데이터에 성별 구분값이 없습니다." />
      </div>
    )
  }
  return (
    <div className="grid gap-4 xl:grid-cols-2">
      <BarDistribution title={`지역별 ${metricName}`} items={data.byRegion} emptyText="원천 데이터에 지역 구분값이 없습니다." />
      <BarDistribution title={`연령대별 ${metricName}`} items={data.byAgeGroup} emptyText="원천 데이터에 연령 구분값이 없습니다." />
      <DonutDistribution title={`보험종류별 ${metricName}`} items={data.byInsuranceType} emptyText="원천 데이터에 보험종류 구분값이 없습니다." />
      <DonutDistribution title={`성별 ${metricName}`} items={data.byGender} emptyText="원천 데이터에 성별 구분값이 없습니다." />
    </div>
  )
}

function GeneralInsuranceMatrix({ items }) {
  const rows = Array.isArray(items) ? items : []
  const coverageLabels = topRows(groupGeneralRows(rows, 'displayCoverageType', 'contractCount'), 'contractCount', 5).map((item) => item.label)
  const objectLabels = topRows(groupGeneralRows(rows, 'displayObjectType', 'premiumAmount'), 'premiumAmount', 5).map((item) => item.label)
  if (!coverageLabels.length || !objectLabels.length) return null
  const valueMap = new Map()
  rows.forEach((row) => {
    const coverage = row.displayCoverageType
    const object = row.displayObjectType
    if (!coverage || !object) return
    const key = `${coverage}|||${object}`
    const prev = valueMap.get(key) || { contractCount: 0, premiumAmount: 0 }
    valueMap.set(key, {
      contractCount: prev.contractCount + toNumber(row.contractCount),
      premiumAmount: prev.premiumAmount + toNumber(row.premiumAmount),
    })
  })
  const maxContract = Math.max(1, ...Array.from(valueMap.values()).map((item) => item.contractCount))
  return (
    <section className="rounded-lg border border-slate-200 bg-white p-4 shadow-sm">
      <SectionHeader icon={ClipboardList} title="담보·물건 조합" />
      <div className="overflow-x-auto">
        <table className="w-full min-w-[720px] border-collapse text-sm">
          <thead>
            <tr className="border-y border-slate-100 bg-slate-50 text-left text-xs font-semibold text-slate-500">
              <th className="px-3 py-2">담보구분</th>
              {objectLabels.map((object) => (
                <th key={object} className="px-3 py-2 text-right">{object}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {coverageLabels.map((coverage) => (
              <tr key={coverage} className="border-b border-slate-100">
                <td className="px-3 py-2 font-semibold text-slate-800">{coverage}</td>
                {objectLabels.map((object) => {
                  const value = valueMap.get(`${coverage}|||${object}`)
                  const opacity = value ? Math.max(0.08, Math.min(0.32, value.contractCount / maxContract * 0.32)) : 0
                  return (
                    <td key={`${coverage}-${object}`} className="px-3 py-2 text-right tabular-nums text-slate-700" style={{ backgroundColor: `rgba(15, 118, 110, ${opacity})` }}>
                      {value ? formatNumber(value.contractCount) : '-'}
                    </td>
                  )
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </section>
  )
}

function buildMarketInsights(tab, data) {
  const metric = data?.primaryMetricTotal > 0
    ? { label: data.primaryMetricLabel, metricTotal: data.primaryMetricTotal }
    : topRow(data?.byMetric, 'metricTotal', 'metric')
  const region = topRow(data?.byRegion)
  const age = topRow(data?.byAgeGroup)
  const insuranceType = topRow(data?.byInsuranceType)
  const coverage = topRow(data?.byCoverageType)
  const objectType = topRow(data?.byObjectType)
  const category = topRow(data?.byCategory)
  const focus = region || age || insuranceType || coverage || objectType || category || metric
  const insights = []
  if (metric) {
    insights.push({
      title: '대표 지표',
      body: `${labelOf(metric, 'metric')} 항목이 ${compactNumber(valueOf(metric))}로 가장 크게 잡힙니다.`,
      icon: TrendingUp,
      iconTone: 'bg-teal-50 text-teal-700',
    })
  }
  if (region) {
    insights.push({
      title: '지역 기준 포인트',
      body: `${labelOf(region)} 지역 데이터가 상위권입니다. 지역별 상담 자료나 조직별 영업 포인트로 활용하기 좋습니다.`,
      icon: Landmark,
      iconTone: 'bg-sky-50 text-sky-700',
    })
  }
  if (age || insuranceType || coverage || objectType || category) {
    const first = age
      ? `${labelOf(age)} 연령대`
      : labelOf(insuranceType || coverage || objectType || category)
    insights.push({
      title: '고객군 힌트',
      body: `${first} 중심으로 ${tab.label} 니즈를 설명하면 시장 기준을 곁들인 상담 흐름을 만들 수 있습니다.`,
      icon: UsersRound,
      iconTone: 'bg-violet-50 text-violet-700',
    })
  }
  return {
    focusLabel: focus ? labelOf(focus) : '',
    insights,
  }
}

function buildNeedsFocusedUsagePoints(tab, data) {
  const age = topRow(data?.byAgeGroup)
  const gender = topRow(data?.byGender)
  const insuranceType = topRow(data?.byInsuranceType)
  const category = topRow(data?.byCategory)
  const ageLabel = age ? labelOf(age) : ''
  const genderLabel = gender ? labelOf(gender) : ''
  const insuranceTypeLabel = insuranceType ? labelOf(insuranceType) : ''
  const categoryLabel = category ? labelOf(category) : ''

  if (tab.id === 'annuity') {
    return [
      {
        title: ageLabel ? `${ageLabel} 연령대의 연금 준비 필요성 환기` : '연령대별 연금 준비 필요성 환기',
        body: '현재 고객의 은퇴까지 남은 기간, 납입 가능 기간, 목표 연금액을 함께 확인하는 질문으로 연결합니다.',
      },
      {
        title: categoryLabel ? `${categoryLabel} 흐름을 활용한 세제·납입 니즈 확인` : '세제적격·납입방법 차이로 니즈 확인',
        body: '세제 혜택, 납입 여력, 장기 유지 가능성을 구분해 고객에게 맞는 개인연금 방향을 좁힙니다.',
      },
      {
        title: '가입이 늦어진 고객군의 준비 공백 점검',
        body: '연금 준비가 늦어진 고객에게는 납입 기간이 짧아질수록 월 납입 부담이 커질 수 있다는 점을 상담 흐름에 반영합니다.',
      },
    ]
  }

  const customerGroupLabel = [ageLabel, genderLabel].filter(Boolean).join(' / ')
  return [
    {
      title: customerGroupLabel ? `${customerGroupLabel} 고객군의 보장 준비 상태 확인` : '고객군별 보장 준비 상태 확인',
      body: '현재 고객의 연령대와 성별을 기준으로 가입 현황을 비교해 부족 보장을 점검하는 질문으로 연결합니다.',
    },
    {
      title: insuranceTypeLabel ? `${insuranceTypeLabel} 가입 흐름을 보장 우선순위로 전환` : '시장 가입 흐름을 보장 우선순위로 전환',
      body: '많이 가입된 보험종류를 그대로 권유하기보다, 고객의 기존 보장과 겹치는지 먼저 확인하는 근거로 사용합니다.',
    },
    {
      title: '가입률이 낮은 구간의 보장 공백 니즈 확인',
      body: '시장 가입률이 낮은 고객군은 미가입 사유, 보험료 부담, 기존 질병 이력 등을 확인하는 대화로 이어갑니다.',
    },
  ]
}

function buildGeneralInsights(data) {
  const coverage = topRow(data?.byCoverageType, 'contractCount')
  const object = topRow(data?.byObjectType, 'premiumAmount')
  const insights = []
  if (coverage) {
    insights.push({
      title: '계약 중심 담보',
      body: `${labelOf(coverage)} 담보가 계약건수 기준 상위입니다. 일반보험 제안 시 우선 확인할 담보군으로 볼 수 있습니다.`,
      icon: ShieldCheck,
      iconTone: 'bg-teal-50 text-teal-700',
    })
  }
  if (object) {
    insights.push({
      title: '보험료 중심 물건',
      body: `${labelOf(object)} 물건구분이 보험료 기준 상위입니다. 사업장 유형별 제안 근거로 활용할 수 있습니다.`,
      icon: Building2,
      iconTone: 'bg-amber-50 text-amber-700',
    })
  }
  insights.push({
    title: '일반보험 연결 후보',
    body: '기업성 재물·배상책임 상담에서 담보와 물건 유형을 함께 보여주면 시장 규모 설명이 쉬워집니다.',
    icon: ClipboardList,
    iconTone: 'bg-sky-50 text-sky-700',
  })
  return {
    focusLabel: object ? labelOf(object) : (coverage ? labelOf(coverage) : ''),
    insights,
  }
}

function buildKosisNonlifeUsagePoints(data) {
  const topType = topRow(data?.byInsuranceType, 'premiumAmount')
  const topCompany = topRow(data?.byCompany, 'premiumAmount')
  const lossCompany = topRow(data?.byCompany, 'lossRatio')
  return [
    {
      title: topType ? `${labelOf(topType)} 종목의 보험료 규모 확인` : '손해보험 종목별 보험료 규모 확인',
      body: '고객의 업종이나 보유 자산이 어떤 손해보험 종목과 맞닿는지 확인하고, 보장 우선순위를 설명하는 자료로 사용합니다.',
    },
    {
      title: topCompany ? `${labelOf(topCompany)} 등 주요 손보사 흐름 비교` : '보험사별 계약·보험료 흐름 비교',
      body: '보험사 선택을 단순 순위가 아니라 계약건수, 수입보험료, 손해율을 함께 보는 비교 관점으로 전환합니다.',
    },
    {
      title: lossCompany ? `${labelOf(lossCompany)} 손해율 지표 확인` : '손해율 지표로 리스크 관점 보강',
      body: '손해율은 가격·인수·보상 흐름을 이해하는 참고 지표로 보고, 고객에게는 보장 필요성과 리스크 설명에만 제한적으로 활용합니다.',
    },
  ]
}

function buildKosisNonlifeInsights(data) {
  const topType = topRow(data?.byInsuranceType, 'premiumAmount')
  const topCompany = topRow(data?.byCompany, 'premiumAmount')
  const contractType = topRow(data?.byInsuranceType, 'contractCount')
  const insights = []
  if (topType) {
    insights.push({
      title: '보험료 중심 종목',
      body: `${labelOf(topType)} 종목의 수입보험료가 상위입니다. 업종·자산별 손해보험 상담에서 시장 규모 근거로 활용할 수 있습니다.`,
      icon: TrendingUp,
      iconTone: 'bg-teal-50 text-teal-700',
    })
  }
  if (contractType) {
    insights.push({
      title: '계약 중심 종목',
      body: `${labelOf(contractType)} 종목은 계약건수 흐름을 함께 볼 만합니다. 고객군의 보유 위험과 실제 가입 흐름을 비교해 보세요.`,
      icon: ClipboardList,
      iconTone: 'bg-sky-50 text-sky-700',
    })
  }
  if (topCompany) {
    insights.push({
      title: '보험사 비교 포인트',
      body: `${labelOf(topCompany)} 등 주요 손보사의 보험료·계약건수·손해율을 한 화면에서 비교해 제안 근거를 정리합니다.`,
      icon: Building2,
      iconTone: 'bg-amber-50 text-amber-700',
    })
  }
  return insights
}

function buildLifeSalesChannelUsagePoints(data) {
  const contractChannel = topRow(data?.byChannel, 'contractCount')
  const premiumChannel = topRow(data?.byChannel, 'premiumAmount')
  const product = topRow(data?.byProduct, 'contractCount')
  return [
    {
      title: contractChannel ? `${labelOf(contractChannel)} 채널의 신계약 흐름 확인` : '채널별 신계약 흐름 확인',
      body: '조직의 주력 채널과 시장 판매 채널을 비교해 리더 미팅이나 캠페인 방향을 잡는 참고 지표로 사용합니다.',
    },
    {
      title: premiumChannel ? `${labelOf(premiumChannel)} 채널의 보험료 기여도 점검` : '채널별 보험료 기여도 점검',
      body: '건수와 보험료가 다르게 움직이는 채널은 상품 믹스, 고객군, 교육 포인트를 나누어 살펴봅니다.',
    },
    {
      title: product ? `${labelOf(product)} 상품군의 채널 차이 연결` : '상품군별 채널 차이 연결',
      body: '보장성/저축성/변액보험 흐름을 분리해 설계사 교육 자료나 리크루팅 메시지에 연결합니다.',
    },
  ]
}

function buildInsurerInsights(rows, selectedMetricLabel) {
  const byCompany = new Map()
  rows.forEach((row) => {
    const name = companyLabel(row)
    if (!name) return
    byCompany.set(name, (byCompany.get(name) || 0) + toNumber(row.metricValue))
  })
  const companyRows = Array.from(byCompany.entries())
    .map(([label, metricTotal]) => ({ label, metricTotal }))
    .sort((a, b) => b.metricTotal - a.metricTotal)
  const topCompany = companyRows[0]
  const topMetric = rows
    .filter((row) => metricLabel(row))
    .sort((a, b) => toNumber(b.metricValue) - toNumber(a.metricValue))[0]
  const insights = []
  if (topCompany) {
    insights.push({
      title: '상위 보험사',
      body: `${labelOf(topCompany)}가 ${selectedMetricLabel || '선택 지표'} 기준 상위입니다. 지표를 바꿔 보험사별 강점을 비교해 보세요.`,
      icon: Building2,
      iconTone: 'bg-sky-50 text-sky-700',
    })
  }
  if (topMetric) {
    const topMetricLabel = metricLabel(topMetric)
    insights.push({
      title: '눈에 띄는 지표',
      body: `${topMetricLabel} 지표에서 ${companyLabel(topMetric) || '해당 보험사'} 값이 크게 잡힙니다.`,
      icon: TrendingUp,
      iconTone: 'bg-teal-50 text-teal-700',
    })
  }
  insights.push({
    title: '비교 리포트 활용',
    body: '생보와 손보를 구분해 주요 영업·재무 지표를 비교하면 보험사 선택 근거를 정리하기 좋습니다.',
    icon: ClipboardList,
    iconTone: 'bg-violet-50 text-violet-700',
  })
  return {
    companyRows,
    focusLabel: topCompany ? labelOf(topCompany) : '',
    insights,
  }
}

function optionLabel(options, value) {
  return options.find((option) => option.id === value)?.label || options[0]?.label || ''
}

function displayInsightReason(value) {
  const text = String(value || '').trim()
  if (!text) return '수집 데이터 기준'
  const hasRawCode = /[A-Za-z][A-Za-z0-9_]*[A-Z_][A-Za-z0-9_]*/.test(text)
  return hasRawCode ? '수집 데이터 기준' : text
}

function filterWorkbenchMaterials(items, topic, customerGroup, context) {
  return items.filter((item) => {
    const topicMatched = topic === 'all' || item.topic === topic
    const customerMatched = customerGroup === 'all' || item.customerTags.includes('all') || item.customerTags.includes(customerGroup)
    const contextMatched = item.usageContextTags.includes('all') || item.usageContextTags.includes(context)
    return topicMatched && customerMatched && contextMatched
  })
}

function workbenchMaterialText(item, context) {
  if (context === 'report') return item.reportText
  if (context === 'briefing') return `${item.title}: ${item.reason}`
  return item.body
}

function TabButton({ tab, active, onClick }) {
  const Icon = tab.icon || BarChart3
  return (
    <button
      type="button"
      role="tab"
      id={`market-insight-tab-${tab.id}`}
      aria-controls={`market-insight-panel-${tab.id}`}
      aria-selected={active}
      tabIndex={active ? 0 : -1}
      className={`inline-flex min-h-[2.5rem] items-center gap-2 whitespace-nowrap border-b-2 px-3 text-sm font-semibold ${
        active
          ? 'border-brand-navy text-brand-navy'
          : 'border-transparent text-slate-500 hover:text-slate-800'
      }`}
      onClick={onClick}
    >
      <Icon className="h-4 w-4" aria-hidden="true" />
      {tab.label}
    </button>
  )
}

function CollectingPanel() {
  return (
    <section className="rounded-lg border border-slate-200 bg-white p-4 text-sm font-semibold text-slate-700 shadow-sm">
      현재 데이터를 수집 중 입니다.
    </section>
  )
}

function SegmentGroup({ label, options, value, onChange }) {
  return (
    <div className="rounded-lg border border-slate-200 bg-white p-3">
      <div className="text-xs font-bold text-slate-500">{label}</div>
      <div className="mt-2 flex flex-wrap gap-2">
        {options.map((option) => {
          const active = option.id === value
          return (
            <button
              key={option.id}
              type="button"
              aria-pressed={active}
              className={`min-h-[2rem] rounded border px-3 text-sm font-semibold ${
                active
                  ? 'border-brand-navy bg-brand-navy text-white'
                  : 'border-slate-200 bg-white text-slate-600 hover:border-slate-300 hover:bg-slate-50'
              }`}
              onClick={() => onChange(option.id)}
            >
              {option.label}
            </button>
          )
        })}
      </div>
    </div>
  )
}

function CounselingInsightWorkbench({ data, onSelectTab }) {
  const [topic, setTopic] = React.useState('all')
  const [customerGroup, setCustomerGroup] = React.useState('all')
  const [context, setContext] = React.useState('consulting')
  const [copiedId, setCopiedId] = React.useState('')

  const allMaterials = React.useMemo(() => {
    const items = Array.isArray(data?.items) ? data.items : []
    return items.map((item) => ({
      id: item.insightKey || String(item.id),
      topic: item.topic || 'market',
      customerTags: Array.isArray(item.customerTags) && item.customerTags.length ? item.customerTags : ['all'],
      usageContextTags: Array.isArray(item.usageContextTags) && item.usageContextTags.length ? item.usageContextTags : ['all'],
      title: item.title,
      body: item.body,
      reason: displayInsightReason(item.reason),
      reportText: item.reportText || `${item.title}. ${item.body}`,
      tabId: item.detailTab,
      sourceName: item.sourceName,
      source: item.sourceLabel || '-',
      referencePeriod: item.sourceReferencePeriod || '',
      collectedAt: item.sourceCollectedAt || '활성 수집 데이터 기준',
    }))
  }, [data])
  const materials = React.useMemo(
    () => filterWorkbenchMaterials(allMaterials, topic, customerGroup, context),
    [allMaterials, topic, customerGroup, context],
  )
  const evidenceRows = React.useMemo(() => {
    const evidence = Array.isArray(data?.evidence) ? data.evidence : []
    return evidence.map((row, index) => {
      const firstItem = allMaterials.find((item) => item.sourceName === row.sourceName)
      return {
        id: row.sourceName || `source-${index}`,
        title: row.sourceLabel || row.sourceName || '-',
        value: `${formatNumber(row.insightCount)}개`,
        label: row.sourceCollectedAt || '활성 수집 데이터 기준',
        referencePeriod: row.referencePeriod || '',
        tabId: firstItem?.tabId,
        source: row.sourceLabel || row.sourceName || '-',
        sourceUrl: row.sourceUrl || '',
        collectedAt: row.sourceCollectedAt || '활성 수집 데이터 기준',
      }
    })
  }, [allMaterials, data])

  const copyReportText = async (item) => {
    const text = `${workbenchMaterialText(item, context)}\n\n출처: ${item.source || '-'} · 기준기간: ${item.referencePeriod || '-'} · 수집일: ${item.collectedAt || '-'}`
    try {
      await navigator.clipboard.writeText(text)
      setCopiedId(item.id)
      window.setTimeout(() => setCopiedId(''), 1500)
    } catch (error) {
      setCopiedId('')
    }
  }

  return (
    <div className="space-y-6">
      <section className="border-b border-slate-200 pb-5">
        <div className="grid gap-3 xl:grid-cols-3">
          <SegmentGroup label="니즈" options={WORKBENCH_TOPIC_OPTIONS} value={topic} onChange={setTopic} />
          <SegmentGroup label="고객군" options={WORKBENCH_CUSTOMER_OPTIONS} value={customerGroup} onChange={setCustomerGroup} />
          <SegmentGroup label="활용처" options={WORKBENCH_CONTEXT_OPTIONS} value={context} onChange={setContext} />
        </div>
      </section>

      <section>
        <SectionHeader icon={MessageSquareText} title="추천 상담 포인트" />
        {materials.length === 0 ? (
          <CollectingPanel />
        ) : (
          <div className="grid gap-3 xl:grid-cols-3">
            {materials.map((item) => (
              <article key={item.id} className="flex min-h-[16rem] flex-col rounded-lg border border-slate-200 bg-white p-4 shadow-sm">
                <div className="flex flex-wrap gap-2">
                  <span className="rounded-full bg-slate-100 px-2 py-1 text-[11px] font-bold text-slate-600">
                    {optionLabel(WORKBENCH_TOPIC_OPTIONS, item.topic)}
                  </span>
                  <span className="rounded-full bg-teal-50 px-2 py-1 text-[11px] font-bold text-teal-700">
                    {item.reason}
                  </span>
                </div>
                <h3 className="mt-3 text-base font-bold leading-6 text-slate-900">{item.title}</h3>
                <p className="mt-2 text-sm leading-6 text-slate-600">{workbenchMaterialText(item, context)}</p>
                <div className="mt-auto border-t border-slate-100 pt-3 text-xs leading-5 text-slate-500">
                  출처: {item.source || '-'} · 기준기간: {item.referencePeriod || '-'} · 수집일: {item.collectedAt || '-'}
                </div>
                <div className="mt-3 flex flex-wrap gap-2">
                  <button
                    type="button"
                    className="inline-flex min-h-[2rem] items-center gap-1 rounded border border-slate-200 px-3 text-xs font-bold text-slate-700 hover:bg-slate-50"
                    onClick={() => onSelectTab?.(item.tabId)}
                  >
                    <BarChart3 className="h-3.5 w-3.5" aria-hidden="true" />
                    근거 보기
                  </button>
                  <button
                    type="button"
                    className="inline-flex min-h-[2rem] items-center gap-1 rounded border border-slate-200 px-3 text-xs font-bold text-slate-700 hover:bg-slate-50"
                    onClick={() => copyReportText(item)}
                  >
                    <ClipboardList className="h-3.5 w-3.5" aria-hidden="true" />
                    {copiedId === item.id ? '복사됨' : '문장 복사'}
                  </button>
                </div>
              </article>
            ))}
          </div>
        )}
      </section>

      <details className="rounded-lg border border-slate-200 bg-white px-4 py-3">
        <summary className="cursor-pointer select-none text-sm font-bold text-slate-800">출처 및 수집 정보</summary>
        <div className="mt-3 divide-y divide-slate-100">
          {evidenceRows.map((row) => (
            <div key={row.id} className="flex flex-wrap items-center justify-between gap-2 py-2 text-xs text-slate-600">
              <div>
                <div className="font-semibold text-slate-800">{row.title}</div>
                <div className="mt-0.5">기준기간 {row.referencePeriod || '-'} · 수집일 {row.collectedAt || '-'}</div>
              </div>
              {row.sourceUrl ? (
                <a className="inline-flex items-center gap-1 font-semibold text-brand-navy hover:underline" href={row.sourceUrl} target="_blank" rel="noreferrer">
                  원천 보기
                  <ExternalLink className="h-3.5 w-3.5" aria-hidden="true" />
                </a>
              ) : null}
            </div>
          ))}
          {evidenceRows.length === 0 ? <div className="py-3 text-xs text-slate-500">표시할 출처 정보가 없습니다.</div> : null}
        </div>
      </details>
    </div>
  )
}

function MarketReferenceSummary({ title = '참고용 시장 요약', children, source, rowCount, referencePeriod, collectedAt }) {
  return (
    <section className="rounded-lg border border-slate-200 bg-white shadow-sm">
      <div className="p-4">
        <SectionHeader icon={TrendingUp} title={title} />
        {children}
      </div>
      <div className="border-t border-slate-100 px-4 py-3">
        <div className="grid gap-3 text-xs text-slate-600 md:grid-cols-4">
          <div><span className="font-bold text-slate-800">출처</span> {source || '-'}</div>
          <div><span className="font-bold text-slate-800">원천 지표수</span> {formatNumber(rowCount)}</div>
          <div><span className="font-bold text-slate-800">기준기간</span> {referencePeriod || '-'}</div>
          <div><span className="font-bold text-slate-800">수집일</span> {collectedAt || '-'}</div>
        </div>
      </div>
    </section>
  )
}

function SourceFooterText({ source, referencePeriod, collectedAt }) {
  return (
    <div className="px-1 pb-1 text-xs leading-5 text-slate-500">
      출처: {source || '-'} · 기준기간: {referencePeriod || '-'} · 수집일: {collectedAt || '-'}
    </div>
  )
}

function MarketStatsPanel({ tab, data }) {
  const version = data?.version
  if (!version) return <CollectingPanel />
  const normalized = normalizeMarketData(tab, data)
  const overview = data?.overview || {}
  const items = normalized.items
  const needsFocused = isNeedsFocusedMarketTab(tab)
  const { focusLabel, insights } = needsFocused
    ? { focusLabel: '', insights: [] }
    : buildMarketInsights(tab, normalized)
  const usagePoints = needsFocused ? buildNeedsFocusedUsagePoints(tab, normalized) : null
  return (
    <div className="space-y-4">
      <UsagePlaybook tab={tab} focusLabel={needsFocused ? '' : focusLabel} points={usagePoints} />
      {!needsFocused ? <InsightCardGrid insights={insights} /> : null}
      {!needsFocused ? (
        <MarketReferenceSummary
          source={tab.source}
          rowCount={overview.rowCount}
          referencePeriod={version.referencePeriod}
          collectedAt={versionDate(version)}
        >
          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
            <StatBox label="기준 기간" value={periodRange(overview, 'minPeriod', 'maxPeriod')} helper={versionLabel(version)} tone="navy" />
            <StatBox label={normalized.primaryMetricLabel || '대표 지표'} value={compactNumber(normalized.primaryMetricTotal)} helper={formatNumber(normalized.primaryMetricTotal)} tone="teal" />
            <StatBox label="기간 수" value={formatNumber(overview.periodCount)} tone="slate" />
            <StatBox label="표시 데이터" value={formatNumber(items.length)} helper="상세 목록 기준" tone="amber" />
          </div>
        </MarketReferenceSummary>
      ) : null}
      <MarketDistributionGrid tab={tab} data={normalized} />
      <MarketStatsTable items={items} />
      {needsFocused ? <SourceFooterText source={tab.source} referencePeriod={version.referencePeriod} collectedAt={versionDate(version)} /> : null}
    </div>
  )
}

function MarketStatsTable({ items }) {
  return (
    <details className="rounded-lg border border-slate-200 bg-white p-4 shadow-sm">
      <summary className="cursor-pointer select-none text-sm font-bold text-slate-900">상세 데이터</summary>
      <div className="mt-4 overflow-x-auto">
        <table className="w-full min-w-[900px] text-sm">
          <thead className="border-y border-slate-100 bg-slate-50 text-left text-xs font-semibold text-slate-500">
            <tr>
              <th className="px-3 py-2">기준</th>
              <th className="px-3 py-2">지역</th>
              <th className="px-3 py-2">성/연령</th>
              <th className="px-3 py-2">보험/담보</th>
              <th className="px-3 py-2">지표</th>
              <th className="px-3 py-2 text-right">값</th>
            </tr>
          </thead>
          <tbody>
            {items.length === 0 ? (
              <tr>
                <td className="px-3 py-4 text-slate-500" colSpan={6}>데이터 없음</td>
              </tr>
            ) : items.map((item) => (
              <tr key={item.id} className="border-b border-slate-100">
                <td className="px-3 py-3 text-slate-700">{formatPeriod(item.displayBasePeriod || item.basePeriod)}</td>
                <td className="px-3 py-3 text-slate-700">{item.displayRegionName || '-'}</td>
                <td className="px-3 py-3 text-slate-700">{[item.displayGenderName, item.displayAgeGroup].filter(Boolean).join(' / ') || '-'}</td>
                <td className="px-3 py-3 text-slate-700">{[item.displayInsuranceType, item.displayCoverageType, item.displayObjectType, item.displayCategoryName].filter(Boolean).join(' / ') || '-'}</td>
                <td className="px-3 py-3 font-semibold text-slate-800">{metricLabel(item) || '-'}</td>
                <td className="px-3 py-3 text-right text-slate-700">{formatNumber(item.metricValue ?? item.metricValueText)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </details>
  )
}

function GeneralInsurancePanel({ tab, data }) {
  const version = data?.version
  if (!version) return <CollectingPanel />
  const normalized = normalizeGeneralData(data)
  const overview = data?.overview || {}
  const items = normalized.items
  const { focusLabel, insights } = buildGeneralInsights(normalized)
  return (
    <div className="space-y-4">
      <UsagePlaybook tab={tab} focusLabel={focusLabel} />
      <InsightCardGrid insights={insights} />
      <MarketReferenceSummary
        title="참고용 일반보험 요약"
        source={tab.source}
        rowCount={overview.rowCount}
        referencePeriod={version.referencePeriod}
        collectedAt={versionDate(version)}
      >
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
          <StatBox label="기준년도" value={periodRange(overview, 'minYear', 'maxYear')} helper={versionLabel(version)} tone="navy" />
          <StatBox label="계약건수" value={compactNumber(overview.contractCount)} helper={formatNumber(overview.contractCount)} tone="teal" />
          <StatBox label="보험료" value={compactNumber(overview.premiumAmount)} helper={formatNumber(overview.premiumAmount)} tone="amber" />
          <StatBox label="표시 데이터" value={formatNumber(items.length)} helper="상세 목록 기준" tone="slate" />
        </div>
      </MarketReferenceSummary>
      <div className="grid gap-4 xl:grid-cols-2">
        <BarDistribution title="담보구분별 계약건수" items={normalized.byCoverageType} valueKey="contractCount" emptyText="원천 데이터에 담보 구분값이 없습니다." />
        <DonutDistribution title="물건구분별 보험료" items={normalized.byObjectType} valueKey="premiumAmount" emptyText="원천 데이터에 물건 구분값이 없습니다." />
      </div>
      <GeneralInsuranceMatrix items={items} />
      <details className="rounded-lg border border-slate-200 bg-white p-4 shadow-sm">
        <summary className="cursor-pointer select-none text-sm font-bold text-slate-900">상세 데이터</summary>
        <div className="mt-4 overflow-x-auto">
          <table className="w-full min-w-[760px] text-sm">
            <thead className="border-y border-slate-100 bg-slate-50 text-left text-xs font-semibold text-slate-500">
              <tr>
                <th className="px-3 py-2">기준년도</th>
                <th className="px-3 py-2">담보구분</th>
                <th className="px-3 py-2">물건구분</th>
                <th className="px-3 py-2 text-right">계약건수</th>
                <th className="px-3 py-2 text-right">보험료</th>
              </tr>
            </thead>
            <tbody>
              {items.length === 0 ? (
                <tr><td className="px-3 py-4 text-slate-500" colSpan={5}>데이터 없음</td></tr>
              ) : items.map((item) => (
                <tr key={item.id} className="border-b border-slate-100">
                  <td className="px-3 py-3 text-slate-700">{item.baseYear || '-'}</td>
                  <td className="px-3 py-3 text-slate-700">{item.displayCoverageType || '-'}</td>
                  <td className="px-3 py-3 text-slate-700">{item.displayObjectType || '-'}</td>
                  <td className="px-3 py-3 text-right text-slate-700">{formatNumber(item.contractCount)}</td>
                  <td className="px-3 py-3 text-right text-slate-700">{formatNumber(item.premiumAmount)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </details>
    </div>
  )
}

function KosisNonlifePanel({ tab, data }) {
  const version = data?.version
  if (!version) return <CollectingPanel />
  const normalized = normalizeKosisNonlifeData(data)
  const items = normalized.items
  if (!items.length) return <CollectingPanel />
  return (
    <div className="space-y-4">
      <UsagePlaybook tab={tab} points={buildKosisNonlifeUsagePoints(normalized)} />
      <InsightCardGrid insights={buildKosisNonlifeInsights(normalized)} />
      <div className="grid gap-4 xl:grid-cols-2">
        <BarDistribution title="보험사별 수입보험료" items={normalized.byCompany} valueKey="premiumAmount" emptyText="보험사별 수입보험료 데이터가 없습니다." />
        <BarDistribution title="종목별 수입보험료" items={normalized.byInsuranceType} valueKey="premiumAmount" emptyText="종목별 수입보험료 데이터가 없습니다." />
        <BarDistribution title="종목별 계약건수" items={normalized.byInsuranceType} valueKey="contractCount" emptyText="종목별 계약건수 데이터가 없습니다." />
        <BarDistribution title="보험사별 손해율" items={normalized.byCompany} valueKey="lossRatio" emptyText="보험사별 손해율 데이터가 없습니다." />
      </div>
      <details className="rounded-lg border border-slate-200 bg-white p-4 shadow-sm">
        <summary className="cursor-pointer select-none text-sm font-bold text-slate-900">상세 데이터</summary>
        <div className="mt-4 overflow-x-auto">
          <table className="w-full min-w-[900px] text-sm">
            <thead className="border-y border-slate-100 bg-slate-50 text-left text-xs font-semibold text-slate-500">
              <tr>
                <th className="px-3 py-2">기준월</th>
                <th className="px-3 py-2">보험사</th>
                <th className="px-3 py-2">종목</th>
                <th className="px-3 py-2">구분</th>
                <th className="px-3 py-2">지표</th>
                <th className="px-3 py-2 text-right">값</th>
                <th className="px-3 py-2">단위</th>
              </tr>
            </thead>
            <tbody>
              {items.map((item) => (
                <tr key={item.id} className="border-b border-slate-100">
                  <td className="px-3 py-3 text-slate-700">{formatPeriod(item.displayBasePeriod || item.basePeriod)}</td>
                  <td className="px-3 py-3 text-slate-700">{item.displayCompanyName || '-'}</td>
                  <td className="px-3 py-3 text-slate-700">{item.displayInsuranceTypeName || '-'}</td>
                  <td className="px-3 py-3 text-slate-700">{item.displayStatusName || '-'}</td>
                  <td className="px-3 py-3 font-semibold text-slate-800">{item.displayMetricName || '-'}</td>
                  <td className="px-3 py-3 text-right text-slate-700">{formatNumber(item.metricValue ?? item.metricValueText)}</td>
                  <td className="px-3 py-3 text-slate-500">{item.unitName || '-'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </details>
      <SourceFooterText source={tab.source} referencePeriod={version.referencePeriod} collectedAt={versionDate(version)} />
    </div>
  )
}

function LifeSalesChannelPanel({ tab, data }) {
  const version = data?.version
  if (!version) return <CollectingPanel />
  const normalized = normalizeLifeSalesChannelData(data)
  const items = normalized.items
  if (!items.length) return <CollectingPanel />
  const usagePoints = buildLifeSalesChannelUsagePoints(normalized)
  return (
    <div className="space-y-4">
      <UsagePlaybook tab={tab} points={usagePoints} />
      <div className="grid gap-4 xl:grid-cols-2">
        <BarDistribution title="채널별 신계약 건수" items={normalized.byChannel} valueKey="contractCount" emptyText="채널별 신계약 건수 데이터가 없습니다." />
        <BarDistribution title="채널별 보험료(백만원)" items={normalized.byChannel} valueKey="premiumAmount" emptyText="채널별 보험료 데이터가 없습니다." />
        <DonutDistribution title="상품군별 신계약 건수" items={normalized.byProduct} valueKey="contractCount" emptyText="상품군별 신계약 건수 데이터가 없습니다." />
        <BarDistribution title="상품군별 보험료(백만원)" items={normalized.byProduct} valueKey="premiumAmount" emptyText="상품군별 보험료 데이터가 없습니다." />
      </div>
      <details className="rounded-lg border border-slate-200 bg-white p-4 shadow-sm">
        <summary className="cursor-pointer select-none text-sm font-bold text-slate-900">상세 데이터</summary>
        <div className="mt-4 overflow-x-auto">
          <table className="w-full min-w-[820px] text-sm">
            <thead className="border-y border-slate-100 bg-slate-50 text-left text-xs font-semibold text-slate-500">
              <tr>
                <th className="px-3 py-2">기준월</th>
                <th className="px-3 py-2">구분</th>
                <th className="px-3 py-2">상품군</th>
                <th className="px-3 py-2">채널</th>
                <th className="px-3 py-2 text-right">값</th>
              </tr>
            </thead>
            <tbody>
              {items.map((item) => (
                <tr key={item.id} className="border-b border-slate-100">
                  <td className="px-3 py-3 text-slate-700">{formatPeriod(item.displayBaseYearMonth || item.baseYearMonth)}</td>
                  <td className="px-3 py-3 font-semibold text-slate-800">{item.displayMetricName || item.metricName || '-'}</td>
                  <td className="px-3 py-3 text-slate-700">
                    {[item.displayProductCategory, item.displayProductDetail]
                      .filter((value, index, arr) => value && arr.indexOf(value) === index)
                      .join(' / ') || '-'}
                  </td>
                  <td className="px-3 py-3 text-slate-700">{item.displayChannelName || item.channelName || '-'}</td>
                  <td className="px-3 py-3 text-right text-slate-700">{formatNumber(item.metricValue ?? item.metricValueText)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </details>
      <SourceFooterText source={tab.source} referencePeriod={version.referencePeriod} collectedAt={versionDate(version)} />
    </div>
  )
}

function InsurerPanel({ tab, data }) {
  const normalized = normalizeInsurerData(data)
  const life = normalized.life || {}
  const nonlife = normalized.nonlife || {}
  const rows = [
    ...(Array.isArray(life.items) ? life.items.map((item) => ({ ...item, sectorLabel: '생보' })) : []),
    ...(Array.isArray(nonlife.items) ? nonlife.items.map((item) => ({ ...item, sectorLabel: '손보' })) : []),
  ]
  const metricOptions = Object.values(rows.reduce((acc, row) => {
    const label = metricLabel(row)
    if (!label) return acc
    acc[label] = acc[label] || { label, count: 0 }
    acc[label].count += 1
    return acc
  }, {})).sort((a, b) => b.count - a.count || a.label.localeCompare(b.label, 'ko-KR'))
  const [selectedMetric, setSelectedMetric] = React.useState('')
  React.useEffect(() => {
    if (!metricOptions.length) {
      if (selectedMetric) setSelectedMetric('')
      return
    }
    if (!metricOptions.some((item) => item.label === selectedMetric)) {
      setSelectedMetric(metricOptions[0].label)
    }
  }, [metricOptions, selectedMetric])
  const activeMetric = selectedMetric || metricOptions[0]?.label || ''
  const selectedRows = activeMetric ? rows.filter((row) => metricLabel(row) === activeMetric) : rows
  const companyCount = new Set(rows.map(companyLabel).filter(Boolean)).size
  const { companyRows, focusLabel, insights } = buildInsurerInsights(selectedRows, activeMetric)
  const hasVersion = Boolean(life.version || nonlife.version)
  if (!hasVersion) return <CollectingPanel />
  return (
    <div className="space-y-4">
      <UsagePlaybook tab={tab} focusLabel={focusLabel} />
      <InsightCardGrid insights={insights} />
      <MarketReferenceSummary
        title="참고용 보험사 비교 요약"
        source={tab.source}
        rowCount={rows.length}
        referencePeriod={(life.version || nonlife.version)?.referencePeriod}
        collectedAt={versionDate(life.version || nonlife.version)}
      >
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
          <StatBox label="보험사 수" value={formatNumber(companyCount)} helper={`생보 ${versionLabel(life.version)}`} tone="navy" />
          <StatBox label="선택 지표" value={activeMetric || '-'} helper={`손보 ${versionLabel(nonlife.version)}`} tone="teal" />
          <StatBox label="상위 보험사" value={focusLabel || '-'} tone="amber" />
          <StatBox label="표시 데이터" value={formatNumber(selectedRows.length)} helper="선택 지표 기준" tone="slate" />
        </div>
      </MarketReferenceSummary>
      <section className="rounded-lg border border-slate-200 bg-white p-4 shadow-sm">
        <SectionHeader icon={BarChart3} title="비교 지표 선택" />
        <select
          className="w-full max-w-md rounded border border-slate-300 bg-white px-3 py-2 text-sm text-slate-800"
          value={activeMetric}
          onChange={(event) => setSelectedMetric(event.target.value)}
        >
          {metricOptions.map((item) => (
            <option key={item.label} value={item.label}>{item.label}</option>
          ))}
        </select>
      </section>
      <div className="grid gap-4 xl:grid-cols-2">
        <BarDistribution title={`${activeMetric || '선택 지표'} 보험사 비교`} items={companyRows} emptyText="선택 지표로 비교할 값이 없습니다." />
      </div>
      <details className="rounded-lg border border-slate-200 bg-white p-4 shadow-sm">
        <summary className="cursor-pointer select-none text-sm font-bold text-slate-900">선택 지표 상세</summary>
        <div className="mt-4 overflow-x-auto">
          <table className="w-full min-w-[860px] text-sm">
            <thead className="border-y border-slate-100 bg-slate-50 text-left text-xs font-semibold text-slate-500">
              <tr>
                <th className="px-3 py-2">구분</th>
                <th className="px-3 py-2">기준</th>
                <th className="px-3 py-2">보험사</th>
                <th className="px-3 py-2">지표</th>
                <th className="px-3 py-2 text-right">값</th>
              </tr>
            </thead>
            <tbody>
              {selectedRows.length === 0 ? (
                <tr><td className="px-3 py-4 text-slate-500" colSpan={5}>데이터 없음</td></tr>
              ) : selectedRows.map((item) => (
                <tr key={`${item.sectorLabel}-${item.id}`} className="border-b border-slate-100">
                  <td className="px-3 py-3 font-semibold text-slate-700">{item.sectorLabel}</td>
                  <td className="px-3 py-3 text-slate-700">{formatPeriod(item.baseYearMonth)}</td>
                  <td className="px-3 py-3 text-slate-700">{companyLabel(item) || '-'}</td>
                  <td className="px-3 py-3 font-semibold text-slate-800">{metricLabel(item) || '-'}</td>
                  <td className="px-3 py-3 text-right text-slate-700">{formatNumber(item.metricValue ?? item.metricValueText)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </details>
    </div>
  )
}

function PendingPanel() {
  return <CollectingPanel />
}

function LoadingPanel() {
  return (
    <section className="rounded-lg border border-slate-200 bg-white p-4 text-sm text-slate-600 shadow-sm">
      <RefreshCw className="mr-2 inline h-4 w-4 animate-spin" aria-hidden="true" />
      데이터를 불러오는 중입니다.
    </section>
  )
}

function ErrorPanel({ message }) {
  return (
    <section className="rounded-lg border border-red-200 bg-red-50 p-4 text-sm text-red-700 shadow-sm">
      {message}
    </section>
  )
}

function InsightDataPanel({ tab, state, onSelectTab }) {
  if (tab.dataMode === 'pending') return <PendingPanel tab={tab} />
  if (tab.dataMode === 'diseaseMedicalFeeStats') {
    return (
      <div className="space-y-4">
        <UsagePlaybook tab={tab} />
        <DiseaseMedicalFeeStatsPage embedded />
      </div>
    )
  }
  if (state.loading) return <LoadingPanel />
  if (state.error) return <ErrorPanel message={state.error} />
  if (tab.dataMode === 'workbench') return <CounselingInsightWorkbench data={state.data} onSelectTab={onSelectTab} />
  if (tab.dataMode === 'market') return <MarketStatsPanel tab={tab} data={state.data} />
  if (tab.dataMode === 'general') return <GeneralInsurancePanel tab={tab} data={state.data} />
  if (tab.dataMode === 'lifeSalesChannel') return <LifeSalesChannelPanel tab={tab} data={state.data} />
  if (tab.dataMode === 'kosisNonlife') return <KosisNonlifePanel tab={tab} data={state.data} />
  if (tab.dataMode === 'insurer') return <InsurerPanel tab={tab} data={state.data} />
  return null
}

async function fetchMarketStats(datasetType) {
  return unwrapApiData(await api.get('/insurance/fsc-open-data/market-stats', {
    ...INLINE_ERROR_REQUEST_CONFIG,
    params: { datasetType, limit: 200, includeRaw: true },
  }))
}

async function fetchGeneralSubscriptions() {
  return unwrapApiData(await api.get('/insurance/fsc-open-data/general-subscriptions', {
    ...INLINE_ERROR_REQUEST_CONFIG,
    params: { limit: 200, includeRaw: true },
  }))
}

async function fetchKosisNonlifeContracts() {
  return unwrapApiData(await api.get('/insurance/fsc-open-data/kosis/nonlife-contracts', {
    ...INLINE_ERROR_REQUEST_CONFIG,
    params: { limit: 200, includeRaw: true },
  }))
}

async function fetchLifeSalesChannels() {
  return unwrapApiData(await api.get('/insurance/fsc-open-data/life-sales-channels', {
    ...INLINE_ERROR_REQUEST_CONFIG,
    params: { limit: 200, includeRaw: true },
  }))
}

async function fetchCompanyMetrics() {
  const [life, nonlife] = await Promise.all([
    api.get('/insurance/fsc-open-data/company-metrics', {
      ...INLINE_ERROR_REQUEST_CONFIG,
      params: { sector: 'life', limit: 200, includeRaw: true },
    }),
    api.get('/insurance/fsc-open-data/company-metrics', {
      ...INLINE_ERROR_REQUEST_CONFIG,
      params: { sector: 'nonlife', limit: 200, includeRaw: true },
    }),
  ])
  return {
    life: unwrapApiData(life),
    nonlife: unwrapApiData(nonlife),
  }
}

async function fetchCounselingInsights() {
  return unwrapApiData(await api.get('/insurance/fsc-open-data/counseling-insights', {
    ...INLINE_ERROR_REQUEST_CONFIG,
    params: { limit: 100 },
  }))
}

async function fetchInsightData(tab) {
  if (tab.dataMode === 'workbench') {
    return fetchCounselingInsights()
  }
  if (tab.dataMode === 'market') {
    return fetchMarketStats(tab.datasetType)
  }
  if (tab.dataMode === 'general') {
    return fetchGeneralSubscriptions()
  }
  if (tab.dataMode === 'lifeSalesChannel') {
    return fetchLifeSalesChannels()
  }
  if (tab.dataMode === 'kosisNonlife') {
    return fetchKosisNonlifeContracts()
  }
  if (tab.dataMode === 'insurer') {
    return fetchCompanyMetrics()
  }
  return null
}

export default function InsuranceMarketInsightsPage() {
  const [searchParams, setSearchParams] = useSearchParams()
  const requestedTab = searchParams.get('tab') || WORKBENCH_TAB.id
  const activeTab = NAV_TABS.some((tab) => tab.id === requestedTab) ? requestedTab : WORKBENCH_TAB.id
  const current = NAV_TABS.find((tab) => tab.id === activeTab) || WORKBENCH_TAB
  const [dataState, setDataState] = React.useState({ loading: false, data: null, error: '' })

  React.useEffect(() => {
    let ignore = false
    if (current.dataMode === 'news' || current.dataMode === 'pending' || current.dataMode === 'diseaseMedicalFeeStats') {
      setDataState({ loading: false, data: null, error: '' })
      return () => { ignore = true }
    }
    setDataState({ loading: true, data: null, error: '' })
    fetchInsightData(current)
      .then((data) => {
        if (!ignore) setDataState({ loading: false, data, error: '' })
      })
      .catch((error) => {
        if (!ignore) setDataState({ loading: false, data: null, error: getApiErrorMessage(error, `${current.label} 데이터를 불러오지 못했습니다.`) })
      })
    return () => {
      ignore = true
    }
  }, [current])

  const selectTab = (tabId) => {
    const next = new URLSearchParams(searchParams)
    if (tabId === WORKBENCH_TAB.id) next.delete('tab')
    else next.set('tab', tabId)
    setSearchParams(next, { replace: true })
  }

  return (
    <div className="w-full max-w-none px-4 py-6 lg:px-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-2 border-l-4 border-brand-navy pl-3">
          <h1 className="text-xl font-bold text-slate-900">보험시장 인사이트</h1>
        </div>
      </div>

      <div className="mt-6 overflow-x-auto border-b border-slate-200">
        <div className="flex min-w-max gap-1" role="tablist" aria-label="보험시장 인사이트">
          {NAV_TABS.map((tab) => (
            <TabButton
              key={tab.id}
              tab={tab}
              active={activeTab === tab.id}
              onClick={() => selectTab(tab.id)}
            />
          ))}
        </div>
      </div>

      {activeTab === 'news' ? (
        <div className="mt-6" role="tabpanel" id="market-insight-panel-news" aria-labelledby="market-insight-tab-news">
          <InsuranceNewsPage embedded />
        </div>
      ) : (
        <div className="mt-6 space-y-4" role="tabpanel" id={`market-insight-panel-${activeTab}`} aria-labelledby={`market-insight-tab-${activeTab}`}>
          <InsightDataPanel tab={current} state={dataState} onSelectTab={selectTab} />
        </div>
      )}
    </div>
  )
}
